import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AttributeRuntimeOptionDialog } from "@/components/catalog/AttributeRuntimeOptionDialog";
import { attributeScalarToOptionString } from "@/lib/catalog/attributeScalarString";
import type { Attribute, AttributeOption, AttributeValue } from "@/lib/catalog/types";
import { cn } from "@/lib/utils";
import { Plus } from "lucide-react";
import { useState } from "react";

function sortOptions(options: AttributeOption[]): AttributeOption[] {
  return [...options].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

type Props = {
  attribute: Attribute;
  value: AttributeValue | undefined;
  onChange: (value: AttributeValue) => void;
  onOptionsUpdated?: (attributeId: string, options: AttributeOption[]) => void;
  triggerClassName?: string;
  placeholder?: string;
  runtimeOpen?: boolean;
  onRuntimeOpenChange?: (open: boolean) => void;
};

export function AttributeSelectControl({
  attribute,
  value,
  onChange,
  onOptionsUpdated,
  triggerClassName,
  placeholder,
  runtimeOpen,
  onRuntimeOpenChange,
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const dialogOpen = runtimeOpen ?? internalOpen;
  const setDialogOpen = onRuntimeOpenChange ?? setInternalOpen;
  const current = attributeScalarToOptionString(value);
  const options = sortOptions(attribute.options ?? []);
  const canRuntimeAdd = attribute.type === "select" && Boolean(attribute.allowRuntimeAdd);

  return (
    <>
      <Select value={current || undefined} onValueChange={(v) => onChange(v)}>
        <SelectTrigger className={cn("glass-input h-10", triggerClassName)}>
          <SelectValue placeholder={placeholder ?? `Select ${attribute.name.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => {
            const optValue = String(opt.value ?? "");
            if (!optValue) return null;
            return (
              <SelectItem key={opt.id} value={optValue}>
                {opt.label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {canRuntimeAdd ? (
        <AttributeRuntimeOptionDialog
          attribute={attribute}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onSuccess={(nextOptions, selectedValue) => {
            onOptionsUpdated?.(attribute.id, nextOptions);
            onChange(selectedValue);
          }}
        />
      ) : null}
    </>
  );
}

export function AttributeSelectRuntimeAddButton({
  attribute,
  onClick,
}: {
  attribute: Attribute;
  onClick: () => void;
}) {
  if (attribute.type !== "select" || !attribute.allowRuntimeAdd) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-4 w-7 shrink-0 text-primary hover:bg-primary/10"
      title={`Add ${attribute.name} option`}
      onClick={onClick}
    >
      <Plus className="h-4 w-4" />
    </Button>
  );
}
